import React, { useState, useEffect } from 'react';
import axios, { API } from '../services/api';
import {
  ChartLineUp, Spinner
} from '@phosphor-icons/react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { MainLayout } from '../components/shared/MainLayout';

const InsightsPage = () => {
  const [logs, setLogs] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [logsRes, insightsRes] = await Promise.all([
          axios.get(`${API}/logs?days=30`),
          axios.get(`${API}/insights`)
        ]);
        setLogs(logsRes.data);
        setInsights(insightsRes.data.insights || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const chartData = logs.slice(0, 14).reverse().map(log => ({
    date: new Date(log.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    mood: log.mood,
    energy: log.energy,
    sleep: log.sleep_hours
  }));

  return (
    <MainLayout>
      <header className="glass-header sticky top-0 z-10 px-6 py-4">
        <h1 className="text-xl font-medium text-slate-900">Insights</h1>
      </header>

      <div className="px-6 py-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size={32} className="animate-spin text-[#235347]" />
          </div>
        ) : logs.length > 0 ? (
          <>
            {/* Mood & Energy Chart */}
            <div className="card-base p-5 mb-6">
              <h2 className="font-medium text-slate-900 mb-4">Mood & Energy (14 days)</h2>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94A3B8' }} tickLine={false} axisLine={false} />
                    <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: '#94A3B8' }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }} />
                    <Line type="monotone" dataKey="mood" stroke="#235347" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="energy" stroke="#4A3B69" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex gap-4 mt-4 justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#235347]" />
                  <span className="text-xs text-slate-500">Mood</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#4A3B69]" />
                  <span className="text-xs text-slate-500">Energy</span>
                </div>
              </div>
            </div>

            {/* Sleep Chart */}
            <div className="card-base p-5 mb-6">
              <h2 className="font-medium text-slate-900 mb-4">Sleep Hours (14 days)</h2>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94A3B8' }} tickLine={false} axisLine={false} />
                    <YAxis domain={[0, 12]} tick={{ fontSize: 10, fill: '#94A3B8' }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }} />
                    <Line type="monotone" dataKey="sleep" stroke="#059669" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Observations */}
            {insights.length > 0 && (
              <div className="mb-6">
                <h2 className="font-medium text-slate-900 mb-3">Observations</h2>
                <div className="space-y-3">
                  {insights.map((insight, idx) => (
                    <div key={idx} className={`card-base p-4 border-l-4 ${
                      insight.severity === 'warning' ? 'border-l-amber-500' :
                      insight.severity === 'positive' ? 'border-l-emerald-500' :
                      'border-l-slate-300'
                    }`}>
                      <p className="text-slate-700">{insight.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <ChartLineUp size={48} className="mx-auto text-slate-300 mb-4" />
            <h2 className="font-medium text-slate-900 mb-2">No data yet</h2>
            <p className="text-slate-500 text-sm">Complete daily check-ins to see your health trends.</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};


export default InsightsPage;
